import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import dayjs from "dayjs";

import { getFirstDayOfYear, initialDate } from "@/service/ApplicationQueries";

export type ArchiveProcedureFilters = {
	startDate: Date;
	endDate: Date;
	selectedUser: number;
};

export type UseArchiveProcedureFiltersOptions = {
	/** When true, sync filter state to URL query params. Defaults to true. */
	syncToUrl?: boolean;
};

export function useArchiveProcedureFilters(
	config: UseArchiveProcedureFiltersOptions = {},
) {
	const syncToUrl = config.syncToUrl !== false;
	const [searchParams, setSearchParams] = useSearchParams();

	const parseDate = useCallback((value: string | null, fallback: Date) => {
		if (!value) return fallback;
		const parsed = dayjs(value, "YYYY-MM-DD").toDate();
		return Number.isNaN(parsed.getTime()) ? fallback : parsed;
	}, []);

	const parseUser = useCallback((value: string | null) => {
		if (!value) return 0;
		const parsed = parseInt(value, 10);
		return Number.isFinite(parsed) ? parsed : 0;
	}, []);

	const [startDate, setStartDate] = useState<Date>(() =>
		syncToUrl
			? parseDate(searchParams.get("start"), getFirstDayOfYear())
			: getFirstDayOfYear(),
	);
	const [endDate, setEndDate] = useState<Date>(() =>
		syncToUrl
			? parseDate(searchParams.get("end"), initialDate())
			: initialDate(),
	);
	const [selectedUser, setSelectedUser] = useState<number>(() =>
		syncToUrl ? parseUser(searchParams.get("user")) : 0,
	);

	useEffect(() => {
		if (!syncToUrl) return;
		const nextStart = parseDate(searchParams.get("start"), getFirstDayOfYear());
		const nextEnd = parseDate(searchParams.get("end"), initialDate());
		const nextUser = parseUser(searchParams.get("user"));

		setStartDate((prev) =>
			prev.getTime() === nextStart.getTime() ? prev : nextStart,
		);
		setEndDate((prev) =>
			prev.getTime() === nextEnd.getTime() ? prev : nextEnd,
		);
		setSelectedUser((prev) => (prev === nextUser ? prev : nextUser));
	}, [parseDate, parseUser, searchParams, syncToUrl]);

	useEffect(() => {
		if (!syncToUrl) return;
		const params = new URLSearchParams();
		if (startDate) params.set("start", dayjs(startDate).format("YYYY-MM-DD"));
		if (endDate) params.set("end", dayjs(endDate).format("YYYY-MM-DD"));
		if (selectedUser) params.set("user", String(selectedUser));
		setSearchParams(params, { replace: true });
	}, [startDate, endDate, selectedUser, setSearchParams, syncToUrl]);

	const setDateRange = useCallback((start: Date, end: Date) => {
		setStartDate(start);
		setEndDate(end);
	}, []);

	// Options shape used by your API query
	const options = useMemo(
		() => ({
			StartDate: startDate ? dayjs(startDate).toDate() : undefined,
			EndDate: endDate
				? dayjs(endDate).startOf("day").add(1, "day").toDate()
				: undefined,
			SelectedUser: selectedUser,
		}),
		[startDate, endDate, selectedUser],
	);

	return {
		filters: { startDate, endDate, selectedUser } as ArchiveProcedureFilters,
		setStartDate,
		setEndDate,
		setSelectedUser,
		setDateRange,
		options,
	};
}

export default useArchiveProcedureFilters;
